import { FEATURE_FLAGS, useFeatureFlags } from "../../../../contexts";

// Every pipeline "role" the HubSpot setup asks about, described as data.
// PipelineRoleCard renders each entry generically — to support a new role,
// add an entry here (its keys must exist in hubspot_customizations on the
// backend) instead of copying JSX.
export const PIPELINE_ROLES = [
  {
    key: "active_deal_pipeline",
    title: "Active Deal Pipeline",
    description:
      "Which pipeline corresponds to active contracts that CS manages?",
    stageGroups: [
      {
        key: "active_deal_stages",
        label: "Active Deal Stages",
        description: "Select all stages that correspond to active deals.",
      },
      {
        key: "no_renewal_stages",
        label: "Rolling Contract Stages",
        description:
          "Select any stages used for rolling or open-ended contracts that don't have a fixed renewal date. Most teams leave this empty.",
        optional: true,
      },
    ],
  },
  {
    key: "pilot_pipeline",
    title: "Pilot Pipeline",
    description:
      "Which pipeline do you use to track pilot / proof-of-concept deals? Pilots are evaluated separately from active subscription deals.",
    featureFlag: FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT,
    warnIfSameAsActive:
      "This is the same pipeline as your active deal pipeline — pilots should usually live in a separate pipeline.",
    stageGroups: [
      {
        key: "pilot_deal_stages",
        label: "Pilot Deal Stages",
        description: "Select all stages that correspond to running pilots.",
      },
    ],
  },
  {
    key: "churned_deal_pipeline",
    title: "Churned Deal Pipeline",
    description:
      "To be able to accurately identify churned deals, which pipeline do you use to track churned deals?",
    stageGroups: [
      {
        key: "churned_deal_stages",
        label: "Churned Deal Stages",
        description: "Select all deal stages that correspond to churned deals.",
      },
      {
        key: "comm_churn_stages",
        label: "Communicated Churn Stages",
        description:
          "Which stages do you use to track a customer informing you that their account will churn?",
        optional: true,
      },
    ],
  },
  {
    key: "renewal_pipeline",
    title: "Renewal Pipeline",
    description: "Which pipeline do you use to track renewal deals?",
    stageGroups: [
      {
        key: "renewal_deal_stages",
        label: "Renewal Deal Stages",
        description: "Select all deal stages that correspond to renewal deals.",
      },
    ],
  },
  {
    key: "upsell_expansion_pipeline",
    title: "Upsell/Expansion Pipeline",
    description:
      "Which pipeline do you use to track upsell and expansion opportunities?",
    stageGroups: [
      {
        key: "upsell_opportunity_stages",
        label: "Upsell Opportunity Stages",
        description:
          "Select all deal stages that correspond to upsell and expansion opportunities.",
      },
      {
        key: "upsell_closed_won_stages",
        label: "Upsell Closed Won Stages",
        description:
          "Select all deal stages that correspond to closed won upsell and expansion deals.",
      },
    ],
  },
];

// Roles visible to the current team (some roles are feature-flagged).
export const useVisibleRoles = () => {
  const { isEnabled } = useFeatureFlags();
  return PIPELINE_ROLES.filter(
    (role) => !role.featureFlag || isEnabled(role.featureFlag),
  );
};

// A role is configured once a pipeline is picked and every required stage
// group has at least one stage selected.
export const getRoleStatus = (role, customizations = {}) => {
  const pipeline = customizations[role.key];
  if (!pipeline?.id) {
    return { configured: false, summary: "Not configured" };
  }

  const missingGroups = role.stageGroups.filter(
    (group) => !group.optional && !(customizations[group.key]?.length > 0),
  );
  if (missingGroups.length > 0) {
    return {
      configured: false,
      summary: `${pipeline.name} · select ${missingGroups
        .map((group) => group.label.toLowerCase())
        .join(", ")}`,
    };
  }

  const stageCount = role.stageGroups.reduce(
    (count, group) => count + (customizations[group.key]?.length || 0),
    0,
  );
  return {
    configured: true,
    summary: `${pipeline.name} · ${stageCount} stage${
      stageCount === 1 ? "" : "s"
    } selected`,
  };
};
